/**
 * useRequireExtension — gate de recursos que exigem a extensão (captura, enhance).
 * Retorna `ensure(action)`: se a extensão estiver presente, executa a ação; senão, abre o
 * popup de instalação (uiStore modal 'extension-install'). Repete o handshake vivo em cada ação
 * para não reutilizar uma capability anterior depois de update/downgrade da extensão.
 */
import { useCallback } from 'react';
import { getExtensionFlow, reconcileExtensionObservation } from '../lib/extensionFlow';
import {
  attemptAutomaticExtensionReload,
  completeExtensionReconnect,
} from '../lib/extensionReconnect';
import { useCaptureStore } from '../stores/captureStore';
import { useUiStore } from '../stores/uiStore';

export const EXTENSION_MODAL_ID = 'extension-install';
export const EXTENSION_RECONNECT_MODAL_ID = 'extension-reconnect';

export function useRequireExtension(): (action: () => void) => Promise<void> {
  const setObservation = useCaptureStore((s) => s.setExtensionObservation);
  const openModal = useUiStore((s) => s.openModal);

  return useCallback(
    async (action: () => void) => {
      const previousFlow = useCaptureStore.getState().extensionFlow;
      const detectedFlow = await getExtensionFlow();
      const observation = reconcileExtensionObservation(previousFlow, detectedFlow);
      setObservation(observation);

      if (observation.connection === 'unavailable') {
        openModal(EXTENSION_MODAL_ID);
        return;
      }
      if (observation.connection === 'refresh-required') {
        const reloading = attemptAutomaticExtensionReload(useCaptureStore.getState());
        if (!reloading) openModal(EXTENSION_RECONNECT_MODAL_ID);
        return;
      }

      // Se esta foi a primeira ação depois da recarga, restaura o overlay salvo antes de
      // continuar. O callback ainda pertence ao overlay atual e pode executar normalmente.
      const pendingWindow = completeExtensionReconnect();
      if (pendingWindow) useCaptureStore.getState().openCaptureWindow(pendingWindow);
      action();
    },
    [setObservation, openModal],
  );
}
